import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState(null);

  // Check for email verification status from URL params
  useEffect(() => {
    const emailVerified = searchParams.get('email_verified');
    const error = searchParams.get('error');
    
    if (emailVerified === 'true') {
      setEmailVerificationStatus({ success: true, message: '¡Tu email ha sido verificado correctamente! Ya puedes iniciar sesión.' });
      toast.success('¡Email verificado correctamente!');
    } else if (emailVerified === 'false') {
      let errorMessage = 'Error verificando email.';
      if (error === 'invalid_token') {
        errorMessage = 'El enlace de verificación es inválido o ya fue usado.';
      } else if (error === 'token_expired') {
        errorMessage = 'El enlace de verificación ha expirado. Por favor solicita uno nuevo.';
      }
      setEmailVerificationStatus({ success: false, message: errorMessage });
      toast.error(errorMessage);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);

      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('¡Bienvenido de vuelta!');
        
        // Redirect based on role
        if (response.data.user.role === 'customer') {
          navigate('/kupons');
        } else if (response.data.user.role === 'business') {
          navigate('/business/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Kredenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="font-manrope font-bold text-3xl mb-2">Bienvenido de vuelta</h1>
          <p className="text-muted-foreground">Inicia sesión en tu kuenta</p>
        </div>

        {/* Email Verification Status Banner */}
        {emailVerificationStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-xl mb-6 flex items-center gap-3 ${
              emailVerificationStatus.success 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}
          >
            {emailVerificationStatus.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            )}
            <p className={`text-sm ${emailVerificationStatus.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
              {emailVerificationStatus.message}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="pl-10 rounded-xl"
                placeholder="tu@email.com"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="password">{t('password')}</Label>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-xs text-local-sage hover:underline"
                data-testid="forgot-password-link"
              >
                {t('forgot_password.link')}
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="pl-10 rounded-xl"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-6"
            data-testid="login-submit-button"
          >
            {loading ? t('logging_in') : t('login')}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            {t('no_account')}{' '}
            <Link to="/register" className="text-local-sage hover:underline" data-testid="register-link">
              {t('register')}
            </Link>
          </p>
        </form>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          onBack={() => setShowForgotPassword(false)}
        />
      </motion.div>
    </div>
  );
};